import React, { useMemo, useEffect, useState, useRef } from 'react';
import AgGridReact from './AgGridReact.jsx';
import { gridThemeNoBorder } from './gridTheme.js';
import { fetchJson } from '../../../helpers/fetch.js';
import { JSONtoArr } from '../../datagrid/hooks/useServerSideData.jsx';
import {DATA_TYPE_DEFINITIONS, getLocaleText} from "./index.jsx";
import LoadingIndicator from "../loading_indicator";
import queryClient from '../../../../query_client.js';

const CommonAggridTable = ({columnDefs, fetchPath, quickFilterText = null, containerStyle={},
  filterModel, gridOptions: initialGridOptions = {}, onGridReady: initialOnGridReady = () => {},
  baseQueryKey = 'commonAggridTableRows', gridRef: initialGridRef = null, ...props}) => {
  const gridRef = initialGridRef || useRef(null);
  const containerRef = useRef(null);
  const fetchBodyRef = useRef(props.fetchBody);

  const createServerSideDatasource = () => ({
    getRows: (params) => {
      const request = JSON.stringify({ ...params.request, ...fetchBodyRef.current });
      const queryKey = [baseQueryKey, fetchPath, request];

      queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchJson(fetchPath, {
          method: 'POST',
          body: request
        }),
        staleTime: 30_000,
        keepPreviousData: true
      }
    ).then (response => {
        if (response){
          params.success({
            rowData: JSONtoArr(response.rows),
            rowCount: response.rowCount,
          });

          if(response.rowCount == 0) {
            params.api.showNoRowsOverlay()
          } else {
            params.api.hideOverlay()
          }
        } else {
          // inform grid request failed
          params.fail();
          params.api.hideOverlay()
        }
      })
      .catch(() => {
        params.fail();
        params.api.hideOverlay();
      });
    }
  });

  useEffect(() => {
    fetchBodyRef.current = props.fetchBody;
  }, [props.fetchBody]);

  const datasourceRef = useRef(null);
  if (!datasourceRef.current) {
    datasourceRef.current = createServerSideDatasource(() => fetchBodyRef.current);
  }

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setGridOption('serverSideDatasource', datasourceRef.current);
  }, []);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.refreshServerSide?.({ purge: true });
    api.refreshServerSideStore?.({ purge: true });
  }, [props.fetchBody]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    api.refreshServerSide?.({ purge: true });
    api.refreshServerSideStore?.({ purge: true });
  }, [props.reloadClick]);

  const onGridReady = (params) => {
    gridRef.current.api = params.api;
    gridRef.current.api.setGridOption("serverSideDatasource", createServerSideDatasource());
    resizeGrid();
    initialOnGridReady?.(params);
  };

  const buildFilterModel = () => {
    if (!filterModel && !quickFilterText) return null;

    let newFilterModel = {};
    if (quickFilterText) {
      newFilterModel.global_search = {
        filter: quickFilterText,
        filterType: 'text',
        type: 'contains',
      };
    }
    if (filterModel) {
      newFilterModel = { ...newFilterModel, ...filterModel };
    }
    return newFilterModel;
  };

  useEffect(() => {
    let retryTimer;
    let applyTimer;

    const tryApply = () => {
      const api = gridRef.current?.api;
      if (!api || api.setFilterModel == null) {
        retryTimer = setTimeout(tryApply, 50);
        return;
      }

      const delay = quickFilterText ? 1500 : 0;
      applyTimer = setTimeout(() => {
        api.setFilterModel(buildFilterModel());
      }, delay);
    };

    tryApply();

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(applyTimer);
    };
  }, [JSON.stringify(filterModel), quickFilterText]);


  const gridOptions = useMemo(() => {
    return {
      onGridReady,
      rowModelType: 'serverSide',
      ...initialGridOptions,
      ...DATA_TYPE_DEFINITIONS
    };
  }, []);

  const resizeGrid = () => {
    gridRef.current.api?.sizeColumnsToFit();
  };

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!gridRef.current?.api) return;
      resizeGrid();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const loadingOverlayComponent = useMemo(() => {
    return LoadingIndicator;
  }, []);

  const agColumnDefs = useMemo(() => {
    return columnDefs;
  }, [columnDefs]);

  return (
    <div
      className="grid-theme"
      ref={containerRef}
      style={{
        width: '100%',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        paddingBottom: '5rem',
        height: 'calc(100vh - 180px)',
        ...containerStyle
      }}
    >
      <AgGridReact theme={gridThemeNoBorder}
        columnDefs={agColumnDefs}
        gridOptions={gridOptions}
        ref={gridRef}
        loadingOverlayComponent={loadingOverlayComponent}
        getLocaleText={getLocaleText}
      />
    </div>
  );
}

export default CommonAggridTable;